import { motion } from "motion/react";
import { Shield, Lightbulb, Target, ChevronDown, Eye, Settings, TrendingUp, BookOpen, LogOut } from "lucide-react";
import { AgentFlowDiagram } from "@/components/transparency";
import { EVALUATION_CRITERIA } from "@/data/transparency";
import Header from "@/components/navigation/Header";

export default function TransparencyPage() {
  return (
    <div className="min-h-screen bg-[#F9F7F5]">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-[#063F6B] overflow-hidden flex items-center justify-center min-h-[60vh]">
        {/* Depth glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[700px] bg-[#124C77]/40 rounded-full blur-[140px]" />
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[250px] bg-[#EAA059]/[0.04] rounded-full blur-[100px]" />
        </div>

        <div className="relative z-10 text-center max-w-3xl mx-auto px-6">
          <motion.div
            className="w-10 h-[2px] bg-[#EAA059] mx-auto mb-8"
            initial={{ width: 0 }}
            animate={{ width: 40 }}
            transition={{ delay: 0.1, duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          />

          <motion.h1
            className="text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-6 leading-[1.1] tracking-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          >
            How We Evaluate AI Vendors
          </motion.h1>

          <motion.p
            className="text-lg text-slate-300/90 leading-relaxed mb-10 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          >
            A complete view of the AI agents, models, and prompts that power our
            vendor evaluation system. No black boxes.
          </motion.p>

          {/* Key Principles */}
          <motion.div
            className="flex flex-wrap justify-center gap-3"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {[
              { icon: Shield, text: "Open Architecture", color: "text-emerald-400" },
              { icon: Lightbulb, text: "Visible Prompts", color: "text-[#EAA059]" },
              { icon: Target, text: "Auditable Logic", color: "text-blue-400" },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.text}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/[0.06] border border-white/[0.08] backdrop-blur-sm"
                >
                  <Icon className={`w-4 h-4 ${item.color}`} />
                  <span className="text-sm text-white/80 font-medium">{item.text}</span>
                </div>
              );
            })}
          </motion.div>

          {/* Scroll indicator */}
          <motion.div
            className="mt-12"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
          >
            <motion.div
              animate={{ y: [0, 6, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              <ChevronDown className="w-5 h-5 text-white/30 mx-auto" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Agent Flow Section */}
      <section id="architecture" className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <div className="w-10 h-[2px] bg-[#EAA059] mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-semibold text-[#063F6B] mb-4 tracking-tight">
              Agent Architecture
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Three specialized AI agents work together to analyze vendor
              websites and generate evaluation reports. Click on any agent to see
              its configuration.
            </p>
          </motion.div>

          <AgentFlowDiagram />
        </div>
      </section>

      {/* Evaluation Framework Section */}
      <section className="py-24 px-6 bg-[#F9F7F5]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <div className="w-10 h-[2px] bg-[#EAA059] mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-semibold text-[#063F6B] mb-4 tracking-tight">
              The 6-Criterion Framework
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Every vendor is evaluated against six dimensions that assess both
              immediate utility and long-term organizational capacity building.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {EVALUATION_CRITERIA.map((criterion, index) => {
              const criterionColors: Record<string, string> = {
                SEE: "#4DA6D4", CHANGE: "#9B4FD4", ADAPT: "#5BBFB5",
                USE: "#C47B9F", LEARN: "#5C6BC0", EXIT: "#C4785A",
              };
              const criterionIcons: Record<string, React.ComponentType<{ className?: string }>> = {
                SEE: Eye, CHANGE: Settings, ADAPT: TrendingUp,
                USE: Target, LEARN: BookOpen, EXIT: LogOut,
              };
              const accent = criterionColors[criterion.id] || "#D4893E";
              const CriterionIcon = criterionIcons[criterion.id] || Eye;
              return (
                <motion.div
                  key={criterion.id}
                  className="group p-7 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 hover:-translate-y-1 transition-all duration-300"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ delay: index * 0.08, duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm"
                      style={{ backgroundColor: accent }}
                    >
                      <CriterionIcon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-[#063F6B] tracking-tight">
                      {criterion.name}
                    </h3>
                  </div>
                  <p className="text-sm font-medium mb-2" style={{ color: accent }}>
                    {criterion.subtitle}
                  </p>
                  <p className="text-sm text-gray-600 mb-3">
                    {criterion.question}
                  </p>
                  <p className="text-xs text-gray-400 leading-relaxed">{criterion.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            className="p-8 sm:p-10 rounded-2xl bg-[#F9F7F5] border border-gray-100 shadow-sm"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <div className="w-10 h-[2px] bg-[#EAA059] mx-auto mb-6" />
            <h2 className="text-2xl md:text-3xl font-semibold text-[#063F6B] mb-10 text-center tracking-tight">
              Our Philosophy
            </h2>

            <div className="space-y-8">
              {[
                {
                  title: "The Economics Have Changed",
                  description:
                    "AI SaaS introduces meaningful cost of goods sold (compute, inference, API calls). This creates misaligned incentives\u2014vendors may quietly optimize for reduced usage or cheaper inference paths.",
                },
                {
                  title: "The Moat Has Eroded",
                  description:
                    "Core AI functionality now rests on commodity foundation models. This creates strong vendor incentives to manufacture artificial lock-in through proprietary data formats and tool-specific workflows.",
                },
                {
                  title: "The Stakes Are Organizational",
                  description:
                    "The companies that thrive will develop deep organizational fluency in AI. A vendor solution that delivers a quick win may simultaneously delay the organizational learning that will matter far more in the long run.",
                },
              ].map((item, index) => (
                <motion.div
                  key={item.title}
                  className="flex gap-5"
                  initial={{ opacity: 0, x: -15 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.12, duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
                >
                  <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center bg-[#063F6B] shadow-sm">
                    <span className="text-[#EAA059] font-semibold text-sm">
                      {index + 1}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#063F6B] mb-2 tracking-tight">
                      {item.title}
                    </h3>
                    <p className="text-sm text-gray-500 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer Note */}
      <section className="py-10 px-6 border-t border-gray-100 bg-[#F9F7F5]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm text-gray-400">
            This transparency page itself is an example of our commitment to the
            SEE criterion. We believe you should be able to see how tools work
            before trusting them with important decisions.
          </p>
        </div>
      </section>
    </div>
  );
}
